
import * as React from "react"

const MOBILE_BREAKPOINT = 768

export function useIsMobile() {
  // Set initial state with a function to avoid running it on server-side
  const [isMobile, setIsMobile] = React.useState(() => {
    // Check if window is defined (client-side only)
    if (typeof window !== 'undefined') {
      return window.innerWidth < MOBILE_BREAKPOINT;
    }
    // Default to false if window is not defined
    return false;
  });

  // Use a ref to store the previous value for debouncing
  const lastValue = React.useRef(isMobile);
  
  // Debounce timer ref
  const debounceTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    // Skip if window is not defined
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      // Clear existing timer
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
      }

      // Set new timer for debouncing (50ms)
      debounceTimerRef.current = window.setTimeout(() => {
        const mobile = window.innerWidth < MOBILE_BREAKPOINT;
        // Only update state if the value has changed
        if (mobile !== lastValue.current) {
          lastValue.current = mobile;
          setIsMobile(mobile);
        }
      }, 50);
    }

    window.addEventListener('resize', handleResize);
    
    // Run once on mount
    handleResize();
    
    return () => {
      window.removeEventListener('resize', handleResize);
      // Clear any existing timer
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Memoize the result to prevent unnecessary re-renders
  return React.useMemo(() => isMobile, [isMobile]);
}
